/**
 * Member API — member list (lean picker + full directory) and ADMIN-only CRUD.
 * NOTE: the list GET (GET /api/v1/member) is readable by any authenticated user
 * — it is NOT ADMIN-only. The `enabled` param on useMemberDirectory is a
 * convenience to skip the fetch when a section is hidden, not an authorization
 * gate. Writes (create, update, status patch, delete) are ADMIN-only.
 * There is no hard delete — deactivate via status=INACTIVE.
 * Member writes change scheduler availability, so they also invalidate the
 * portfolio schedule.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { SCHEDULE_KEYS } from './schedule';
import { invalidateDashboards } from './dashboard';
import type { MemberSummary } from '@/types/schedule';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

/** The four built-in role keys. Roles are admin-managed now, so member.role is a
 *  plain string KEY (which may be an admin-created role) — this union is only a
 *  convenience for referencing the built-ins. */
export type AppRole = 'ADMIN' | 'PROJECT_MANAGER' | 'MEMBER' | 'EXECUTIVE';
export type MemberStatus = 'ACTIVE' | 'INACTIVE';

/** Full member directory row (the list endpoint returns all of these). */
export interface Member {
  id: string;
  email: string;
  displayName: string;
  title: string | null;
  role: string;
  status: MemberStatus;
  mustChangePassword: boolean;
  system: boolean;
  accountIds?: string[];
  accounts?: { id: string; name: string }[];
}

/** Create body — email, displayName, role, initialPassword required; email immutable. */
export interface CreateMemberPayload {
  email: string;
  displayName: string;
  role: string;
  initialPassword: string;
  title?: string | null;
  accountIds?: string[];
}

/** Update body — email, displayName, role, status required. */
export interface UpdateMemberPayload {
  email: string;
  displayName: string;
  role: string;
  status: MemberStatus;
  title?: string | null;
  accountIds?: string[];
}

const MEMBER_KEY = ['members'] as const;

// One unfiltered fetch shared by both hooks (same key + same queryFn, so there is
// no cache conflict); each hook narrows the result with its own `select`.
async function fetchAllMembers(): Promise<Member[]> {
  const res = await api.get<ApiEnvelope<Member[]>>('/member');
  return res.data.data;
}

const memberRetry = (failureCount: number, error: unknown) => {
  const e = error as Error & { code?: string };
  // Don't retry on 403 Forbidden — components check `isError` to degrade gracefully.
  if (e.code === 'FORBIDDEN' || e.message?.includes('403')) return false;
  return failureCount < 1;
};

/**
 * Members eligible to be picked as a RESOURCE (CR assignment owner, project /
 * account lead, RAID owner, leave). Hides only the system (break-glass) account.
 * Real administrators are valid resource picks when they carry delivery work.
 */
export function useMembers() {
  return useQuery({
    queryKey: MEMBER_KEY,
    queryFn: fetchAllMembers,
    staleTime: 5 * 60_000,
    retry: memberRetry,
    select: (data: Member[]): MemberSummary[] => data.filter((m) => !m.system),
  });
}

/** Full member directory for the Team Members management table. Hides the system
 *  admin (break-glass account, managed via rotation — not a team resource).
 *  The list GET is readable by any authenticated user; pass enabled=false to
 *  skip the fetch when the section is hidden (not an authorization requirement). */
export function useMemberDirectory(enabled = true) {
  return useQuery({
    queryKey: MEMBER_KEY,
    queryFn: fetchAllMembers,
    enabled,
    staleTime: 5 * 60_000,
    retry: memberRetry,
    select: (data: Member[]): Member[] => data.filter((m) => !m.system),
  });
}

function invalidateMembers(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: MEMBER_KEY });
  void qc.invalidateQueries({ queryKey: SCHEDULE_KEYS.portfolio });
  // Member writes change roster/availability → capacity (workload/bottlenecks) dashboards.
  invalidateDashboards(qc);
}

export function useCreateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMemberPayload) =>
      api.post<ApiEnvelope<Member>>('/member', payload).then((res) => res.data.data),
    onSuccess: () => invalidateMembers(qc),
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMemberPayload }) =>
      api.put<ApiEnvelope<Member>>(`/member/${id}`, payload).then((res) => res.data.data),
    onSuccess: () => invalidateMembers(qc),
  });
}

export function useSetMemberStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: MemberStatus }) =>
      api.patch<ApiEnvelope<Member>>(`/member/${id}/status`, { status }).then((res) => res.data.data),
    onSuccess: () => invalidateMembers(qc),
  });
}

/** Admin reset of another member's password to a temporary value. The backend
 *  forces a change on next login, unlocks the account, and revokes the member's
 *  sessions. Does not touch the members list cache (no roster field changes). */
export function useResetMemberPassword() {
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      api.patch<ApiEnvelope<void>>(`/member/${id}/password`, { newPassword }).then((res) => res.data.data),
  });
}

/** Permanently delete a member (and their leave records). Backend rejects (409)
 *  if the member is still referenced by assignments / ownership / authored reports. */
export function useDeleteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<ApiEnvelope<void>>(`/member/${id}`).then((res) => res.data.data),
    onSuccess: () => invalidateMembers(qc),
  });
}
